import React, { createContext, useContext, useState, useEffect } from 'react';
import { api } from '../App';
import { setCache } from '../utils/apiCache';

const AuthContext = createContext();

// Prefetch commonly needed data after auth (non-blocking)
function prefetchAppData() {
  api.get('/law-categories').then(r => setCache('law-categories', r.data.categories, 300_000)).catch(() => {});
}

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [accessStatus, setAccessStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const response = await api.get('/auth/me');
        const userData = response.data;
        setUser(userData);
        setAccessStatus(userData.has_access === false ? (userData.access_reason || 'no_access') : 'active');

        // Use inline subscription_info from /auth/me (avoids second API call)
        if (userData.subscription_info) {
          setSubscriptionStatus(userData.subscription_info);
        }
        // Prefetch commonly needed data in background
        prefetchAppData();
      } catch (error) {
        // Only clear session on explicit 401 (invalid/expired token)
        // Do NOT clear on network errors (502/503/timeout) from container restarts
        const status = error.response?.status;
        const detail = error.response?.data?.detail || '';
        if (status === 401 && (detail.toLowerCase().includes('expired') || detail.toLowerCase().includes('invalid token'))) {
          localStorage.removeItem('token');
          setUser(null);
          setSubscriptionStatus(null);
          setAccessStatus(null);
        } else {
          // Network error or server down — keep token, retry later
          console.warn('[AuthContext] Backend unreachable, keeping session:', error.message);
        }
      }
    }
    setLoading(false);
  };

  const getDeviceFingerprint = () => {
    let fingerprint = localStorage.getItem('device_fingerprint');
    if (!fingerprint) {
      const nav = navigator;
      const screen = window.screen;
      const deviceData = [
        nav.userAgent, nav.language, screen.width, screen.height,
        screen.colorDepth, new Date().getTimezoneOffset(),
        !!window.sessionStorage, !!window.localStorage
      ].join('|');
      let hash = 0;
      for (let i = 0; i < deviceData.length; i++) {
        const char = deviceData.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash;
      }
      fingerprint = 'device_' + Math.abs(hash) + '_' + Date.now();
      localStorage.setItem('device_fingerprint', fingerprint);
    }
    return fingerprint;
  };

  const login = async (email, password) => {
    try {
      const deviceInfo = getDeviceFingerprint();
      const response = await api.post('/auth/login', { email, password, device_info: deviceInfo });
      if (response.data.token) {
        localStorage.setItem('token', response.data.token);
        setUser(response.data.user);
        // Fetch subscription status in background (non-blocking)
        api.get('/auth/subscription-status').then(r => setSubscriptionStatus(r.data)).catch(() => {});
      }
      return response.data;
    } catch (error) {
      if (error.response?.status === 403 && error.response?.data?.detail === 'device_limit_exceeded') {
        const customError = new Error('Device limit exceeded. This account is already being used on 2 other devices. Please log out from another device to continue.');
        customError.isDeviceLimitError = true;
        throw customError;
      }
      throw error;
    }
  };

  const register = async (email, password, full_name, referral_token = '') => {
    try {
      const payload = { email, password, full_name };
      if (referral_token && referral_token.trim()) {
        payload.referral_code = referral_token.trim();
      }
      const response = await api.post('/auth/register', payload);
      if (response.data.token) {
        localStorage.setItem('token', response.data.token);
        setUser(response.data.user);
        localStorage.removeItem('lexprocure_onboarding_completed');
        api.get('/auth/subscription-status').then(r => setSubscriptionStatus(r.data)).catch(() => {});
      }
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const verifyAndLogin = async (email, password, accessCode) => {
    try {
      const deviceInfo = getDeviceFingerprint();
      const response = await api.post('/auth/verify-access-code', {
        email,
        password,
        access_code: accessCode,
        device_info: deviceInfo
      });
      if (response.data.token) {
        localStorage.setItem('token', response.data.token);
        setUser(response.data.user);
        setAccessStatus('active');
        localStorage.removeItem('lexprocure_onboarding_completed');
        api.get('/auth/subscription-status').then(r => setSubscriptionStatus(r.data)).catch(() => {});
      }
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const resendVerification = async (email) => {
    try {
      const response = await api.post('/auth/resend-verification', { email });
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const logout = async () => {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      console.error('[AuthContext] logout error:', error);
    } finally {
      localStorage.removeItem('token');
      setUser(null);
      setSubscriptionStatus(null);
      setAccessStatus(null);
    }
  };

  const value = {
    user,
    subscriptionStatus,
    accessStatus,
    login,
    register,
    verifyAndLogin,
    resendVerification,
    logout,
    loading,
    checkAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
